import React, { useState, useContext, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useLocation, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import useAuthNavigation from '../hooks/useAuthNavigation';
import { User, Lock, Users, Calendar, Package, Sparkles } from 'lucide-react';

const LoginPage = () => {
  const location = useLocation();
  const { login, currentUser, error: authError } = useContext(AuthContext);
  const { navigateAfterLogin } = useAuthNavigation();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loginError, setLoginError] = useState(''); // Estado para el error de credenciales
  const [isLoading, setIsLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  // Si ya hay un usuario autenticado, redirigir al dashboard
  useEffect(() => {
    if (currentUser) {
      navigateAfterLogin(location.state?.from);
    }
  }, [currentUser, navigateAfterLogin, location]);

  const validate = (values) => {
    const errs = {};
    if (!values.username || values.username.trim().length === 0) {
      errs.username = 'Ingresa tu usuario';
    }
    if (!values.password || values.password.length === 0) {
      errs.password = 'Ingresa tu contraseña';
    }
    return errs;
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const errs = validate(credentials);
    setFieldErrors((prev) => ({ ...prev, [name]: errs[name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setLoginError(''); // Limpiar errores previos
    
    // Validación inline
    const errs = validate(credentials);
    if (Object.keys(errs).length > 0) {
      setTouched({ username: true, password: true });
      setFieldErrors(errs);
      setIsLoading(false);
      return;
    }

    try {
      await login(credentials);
      toast.success('¡Inicio de sesión exitoso!');
      navigateAfterLogin(location.state?.from);
    } catch (err) {
      console.error(err);
      // Verificar el tipo de error para mensajes más específicos
      if (err.response?.status === 401 || err.response?.status === 400) {
        setLoginError('El usuario o la contraseña son incorrectos.');
      } else if (err.message === 'Network Error' || !err.response) {
        setLoginError('Error de conexión. Inténtalo más tarde.');
      } else {
        setLoginError('El usuario o la contraseña son incorrectos.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ============================================= */}
      {/* SECCIÓN IZQUIERDA - FORMULARIO */}
      {/* ============================================= */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24 bg-white">
        <div className="mx-auto w-full max-w-md">
          {/* Logo y título */}
          <div className="text-center mb-10">
            <img
              src="/logo-symbol.png"
              alt="EstetikFlow"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="text-4xl font-bold text-[#1F7A6F] tracking-tight">
              EstetikFlow
            </h1>
            <p className="mt-3 text-gray-500 text-base">
              Gestión inteligente para tu clínica.
            </p>
          </div>

          {/* Formulario */}
          <form onSubmit={handleSubmit} action="#" className="space-y-6">
            {/* Mensaje de error de credenciales */}
            {loginError && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
                {/* Ícono de Alerta (SVG) */}
                <svg className="w-5 h-5 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-red-600 font-medium">
                  {loginError}
                </span>
              </div>
            )}

            {/* Campo Usuario */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Usuario
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  className={`block w-full pl-12 pr-4 py-3.5 border ${
                    touched.username && fieldErrors.username 
                      ? 'border-red-400 ring-2 ring-red-100' 
                      : 'border-gray-200'
                  } rounded-xl text-gray-900 placeholder-gray-400 bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] focus:bg-white transition-all duration-200`}
                  placeholder="Nombre de usuario"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  onBlur={handleBlur}
                />
              </div>
              {touched.username && fieldErrors.username && (
                <p className="mt-2 text-sm text-red-500">{fieldErrors.username}</p>
              )}
            </div>

            {/* Campo Contraseña */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className={`block w-full pl-12 pr-4 py-3.5 border ${
                    touched.password && fieldErrors.password 
                      ? 'border-red-400 ring-2 ring-red-100' 
                      : 'border-gray-200'
                  } rounded-xl text-gray-900 placeholder-gray-400 bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#2A9D8F] focus:border-[#2A9D8F] focus:bg-white transition-all duration-200`}
                  placeholder="••••••••"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  onBlur={handleBlur}
                />
              </div>
              {touched.password && fieldErrors.password && (
                <p className="mt-2 text-sm text-red-500">{fieldErrors.password}</p>
              )}
            </div>

            {/* Link Olvidaste Contraseña */}
            <div className="flex justify-end mt-2 mb-6">
              <Link 
                to="/auth/forgot-password" 
                className="text-sm font-medium text-gray-500 hover:text-[#E76F51] transition-colors"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            {/* Botón de login */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center py-4 px-6 border border-transparent rounded-xl text-base font-semibold text-white bg-[#2A9D8F] hover:bg-[#E76F51] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2A9D8F] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg shadow-[#2A9D8F]/30 hover:shadow-xl hover:shadow-[#E76F51]/40 transform hover:-translate-y-0.5 active:translate-y-0"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Iniciando sesión...
                  </>
                ) : (
                  'Iniciar Sesión'
                )}
              </button>
            </div>

            {/* Link de registro */}
            <div className="text-center">
              <p className="text-sm text-gray-500">
                ¿No tienes cuenta?{' '}
                <Link 
                  to="/app/registro" 
                  className="font-medium text-[#2A9D8F] hover:text-[#E76F51] transition-colors duration-200"
                >
                  Crea una aquí
                </Link>
              </p>
            </div>
          </form>

          {/* Footer */}
          <div className="mt-12 text-center">
            <p className="text-xs text-gray-400">
              © 2025 EstetikFlow. Gestión inteligente para clínicas.
            </p>
          </div>
        </div>
      </div>

      {/* ============================================= */}
      {/* SECCIÓN DERECHA - VISUAL/BRANDING */}
      {/* ============================================= */}
      <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-[#F4E1D2] to-[#FFE8D6]">
        {/* Patrón decorativo sutil */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 right-20 w-64 h-64 bg-[#2A9D8F]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-32 left-16 w-48 h-48 bg-[#E76F51]/10 rounded-full blur-3xl"></div>
        </div>
        
        {/* Contenido del panel derecho */}
        <div className="relative flex flex-col justify-center px-16 xl:px-20">
          <div className="max-w-lg">
            {/* Encabezado */}
            <div className="mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full mb-6">
                <Sparkles className="h-4 w-4 text-[#E76F51]" />
                <span className="text-sm font-medium text-gray-700">Plataforma Integral</span>
              </div>
              <h3 className="text-3xl xl:text-4xl font-bold text-gray-800 mb-4 leading-tight">
                Bienvenido a EstetikFlow
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                La plataforma integral que optimiza tu tiempo y mejora la experiencia de tus pacientes.
              </p>
            </div>

            {/* Features */}
            <div className="space-y-5">
              <div className="flex items-start group">
                <div className="flex-shrink-0 w-12 h-12 bg-[#2A9D8F] rounded-xl flex items-center justify-center mr-4 shadow-lg shadow-[#2A9D8F]/20 group-hover:scale-105 transition-transform duration-200">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-gray-800 font-semibold mb-1">Gestión de Pacientes</h4>
                  <p className="text-gray-600 text-sm">Administra fichas clínicas y historiales médicos completos</p>
                </div>
              </div>

              <div className="flex items-start group">
                <div className="flex-shrink-0 w-12 h-12 bg-[#2A9D8F] rounded-xl flex items-center justify-center mr-4 shadow-lg shadow-[#2A9D8F]/20 group-hover:scale-105 transition-transform duration-200">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-gray-800 font-semibold mb-1">Sistema de Citas</h4>
                  <p className="text-gray-600 text-sm">Calendario inteligente para programar y gestionar citas</p>
                </div>
              </div>

              <div className="flex items-start group">
                <div className="flex-shrink-0 w-12 h-12 bg-[#2A9D8F] rounded-xl flex items-center justify-center mr-4 shadow-lg shadow-[#2A9D8F]/20 group-hover:scale-105 transition-transform duration-200">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="text-gray-800 font-semibold mb-1">Control de Inventario</h4>
                  <p className="text-gray-600 text-sm">Seguimiento de insumos médicos y alertas de stock</p>
                </div>
              </div>
            </div>

            {/* Testimonio / Quote */}
            <div className="mt-10 p-6 bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#2A9D8F] to-[#238578] rounded-full flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-gray-800 font-semibold text-sm">Solución Innovadora</p>
                  <p className="text-gray-500 text-xs">Desarrollado por profesionales</p>
                </div>
              </div>
              <p className="text-gray-700 italic leading-relaxed">
                "Sistema adaptable diseñado para clínicas de estética y bienestar, 
                con funcionalidades pensadas para optimizar tu tiempo y mejorar 
                la atención al paciente."
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;